import {useQueryClient} from '@tanstack/react-query';
import {Fragment} from 'react';
import {useTranslation} from 'react-i18next';

import {FormShell} from '@/components';
import {
  RosterPane,
  RosterPaneHeader,
  RosterSidebarLayout,
  useRosterSelection
} from '@/components/roster';
import {useShowErrors} from '@/features/trust-accounts/activation/hooks';
import {trustAccountKeys} from '@/features/trust-accounts/hooks';

import {SellerDetailsRow} from './SellerDetailsRow';
import {SELLER_ROSTER_PREFIX, useSellerDetailsForm, useSellerSidebarItems} from '../hooks';
import {findFirstIncompleteRow} from '../utils';

import type {FormStateSnapshot} from '@/hooks';
import type {TrustSellerDetailsDTO} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

interface SellerDetailsStepFormProps {
  trustAccountId: string;
  sellers: TrustSellerDetailsDTO[] | undefined;
  isLoading: boolean;
  isError: boolean;
  readOnly?: boolean;
  isActive?: boolean;
  documentsReadOnly?: boolean;
  onFormStateChange?: (state: FormStateSnapshot) => void;
}

export const SellerDetailsStepForm: FC<SellerDetailsStepFormProps> = ({
  trustAccountId,
  sellers,
  isLoading,
  isError,
  readOnly = false,
  isActive = true,
  documentsReadOnly,
  onFormStateChange
}) => {
  const queryClient = useQueryClient();
  const showErrors = useShowErrors();

  const handleDocumentChange = () => {
    void queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(trustAccountId)});
  };

  const {
    form,
    sellerFields,
    handleAddSeller,
    removeSeller,
    isAddingSeller,
    handleSave,
    saveError,
    aiFields,
    sellerAiState,
    markFieldModified,
    stopSellerAi,
    fieldErrors,
    allowsMultipleSellerSideParties,
    asksSellerSideRelationshipQuestions,
    asksSellerSideRole,
    sellerRoleOptions
  } = useSellerDetailsForm({
    trustAccountId,
    sellers,
    isLoading,
    isError,
    isReadOnly: readOnly,
    canCreateInitialSeller: isActive && !readOnly,
    onFormStateChange
  });

  const renderRow = (index: number) => {
    const {isProcessing, suggestionsCount} = sellerAiState(index);

    return (
      <SellerDetailsRow
        form={form}
        index={index}
        trustAccountId={trustAccountId}
        readOnly={readOnly}
        asksRelationshipQuestions={asksSellerSideRelationshipQuestions}
        asksSellerRole={asksSellerSideRole}
        sellerRoleOptions={sellerRoleOptions}
        fieldErrors={fieldErrors}
        showErrors={showErrors}
        aiFields={aiFields}
        isProcessing={isProcessing}
        suggestionsCount={suggestionsCount}
        markFieldModified={markFieldModified}
        onFillManually={() => stopSellerAi(index)}
        documentsReadOnly={documentsReadOnly}
        onDocumentChange={handleDocumentChange}
      />
    );
  };

  return (
    <FormShell
      id="developer-details-form"
      className={allowsMultipleSellerSideParties ? 'flex flex-col' : 'flex flex-col gap-6'}
      errorClassName={allowsMultipleSellerSideParties ? 'mx-6 mt-4' : undefined}
      onSubmit={handleSave}
      saveError={saveError}
    >
      {allowsMultipleSellerSideParties ? (
        <SellerRoster
          form={form}
          sellerFields={sellerFields}
          fieldErrors={fieldErrors}
          sellerAiState={sellerAiState}
          showErrors={showErrors}
          isLoading={isLoading}
          isLocked={readOnly || isAddingSeller}
          onAdd={handleAddSeller}
          onRemove={removeSeller}
          renderRow={renderRow}
        />
      ) : (
        sellerFields.map((field, index) => <Fragment key={field.id}>{renderRow(index)}</Fragment>)
      )}
    </FormShell>
  );
};

type SellerDetailsFormState = ReturnType<typeof useSellerDetailsForm>;

interface SellerRosterProps {
  form: SellerDetailsFormState['form'];
  sellerFields: SellerDetailsFormState['sellerFields'];
  fieldErrors: SellerDetailsFormState['fieldErrors'];
  sellerAiState: SellerDetailsFormState['sellerAiState'];
  showErrors: boolean;
  isLoading: boolean;
  isLocked: boolean;
  onAdd: () => Promise<void>;
  onRemove: (index: number) => void;
  renderRow: (index: number) => ReactNode;
}

const SellerRoster: FC<SellerRosterProps> = ({
  form,
  sellerFields,
  fieldErrors,
  sellerAiState,
  showErrors,
  isLoading,
  isLocked,
  onAdd,
  onRemove,
  renderRow
}) => {
  const {t} = useTranslation('trustAccounts');
  const {selectedIndex, select, selectAfterAdd, adjustAfterRemove} = useRosterSelection(
    sellerFields.length,
    showErrors ? findFirstIncompleteRow(fieldErrors) : null
  );
  const items = useSellerSidebarItems({
    form,
    sellerFields,
    fieldErrors,
    showErrors,
    isExtracting: index => sellerAiState(index).isProcessing
  });

  const handleAdd = () => {
    selectAfterAdd();
    void onAdd();
  };

  const handleRemove = (index: number) => {
    onRemove(index);
    adjustAfterRemove(index);
  };

  return (
    <RosterSidebarLayout
      borders="none"
      title={t(`${SELLER_ROSTER_PREFIX}.sidebarTitle`)}
      items={items}
      selectedIndex={selectedIndex}
      onSelect={select}
      onAdd={handleAdd}
      addLabel={t(`${SELLER_ROSTER_PREFIX}.addSeller`)}
      addDisabled={isLocked}
      summary={
        sellerFields.length > 0
          ? t(`${SELLER_ROSTER_PREFIX}.sellersCount`, {count: sellerFields.length})
          : undefined
      }
      loading={
        isLoading
          ? {
              sidebarLabel: t(`${SELLER_ROSTER_PREFIX}.loading.sidebarLabel`),
              title: t(`${SELLER_ROSTER_PREFIX}.loading.title`),
              subtitle: t(`${SELLER_ROSTER_PREFIX}.loading.subtitle`)
            }
          : undefined
      }
      emptyState={{
        title: t(`${SELLER_ROSTER_PREFIX}.empty.title`),
        subtitle: t(`${SELLER_ROSTER_PREFIX}.empty.subtitle`)
      }}
    >
      {items.map((item, index) => (
        // Index in the key remounts shifted sellers: nested useFieldArray names can't change in place.
        <RosterPane key={`${item.id}-${index}`} active={index === selectedIndex}>
          <RosterPaneHeader
            title={item.title}
            typeLabel={item.subtitle}
            status={item.status}
            positionLabel={t(`${SELLER_ROSTER_PREFIX}.sellerPosition`, {
              current: index + 1,
              total: sellerFields.length
            })}
            removeLabel={t(`${SELLER_ROSTER_PREFIX}.remove`)}
            onRemove={isLocked ? undefined : () => handleRemove(index)}
          />
          {renderRow(index)}
        </RosterPane>
      ))}
    </RosterSidebarLayout>
  );
};
